<template>
  <Card class="person-card">
    <template #content>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="person-avatar">
            <Avatar
              :label="personInitials"
              size="large"
              :style="{ backgroundColor: avatarColor }"
            />
          </div>
          <div class="person-info">
            <h3 class="text-lg font-semibold">{{ person.firstName }} {{ person.lastName }}</h3>
            <p class="text-sm text-gray-600">{{ person.email }}</p>
            <div class="flex items-center gap-2 mt-1">
              <Tag
                v-if="person.type"
                :value="formatPersonType(person.type)"
                :severity="getPersonTypeSeverity(person.type)"
                size="small"
              />
              <Tag
                v-if="person.status"
                :value="formatStatus(person.status)"
                :severity="getStatusSeverity(person.status)"
                size="small"
              />
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            icon="pi pi-eye"
            size="small"
            text
            @click="$emit('view', person)"
            v-tooltip="'View Details'"
          />
          <Button
            icon="pi pi-pencil"
            size="small"
            text
            @click="$emit('edit', person)"
            v-tooltip="'Edit'"
          />
          <Button
            icon="pi pi-trash"
            size="small"
            text
            severity="danger"
            @click="$emit('delete', person)"
            v-tooltip="'Delete'"
          />
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Person, PersonType, PersonStatus } from '@/types/Person'

// Components
import Avatar from 'primevue/avatar'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Tag from 'primevue/tag'

interface Props {
  person: Person
}

const props = defineProps<Props>()

const emit = defineEmits<{
  view: [person: Person]
  edit: [person: Person]
  delete: [person: Person]
}>()

const personInitials = computed(() => {
  const first = props.person.firstName?.charAt(0) || ''
  const last = props.person.lastName?.charAt(0) || ''
  return (first + last).toUpperCase()
})

const avatarColor = computed(() => {
  // Generate a consistent color based on the person's name
  const name = props.person.firstName + props.person.lastName
  const colors = [
    '#ef4444',
    '#f97316',
    '#f59e0b',
    '#eab308',
    '#84cc16',
    '#22c55e',
    '#10b981',
    '#14b8a6',
    '#06b6d4',
    '#0ea5e9',
    '#3b82f6',
    '#6366f1',
    '#8b5cf6',
    '#a855f7',
    '#d946ef',
    '#ec4899',
    '#f43f5e',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
})

function formatPersonType(type: PersonType): string {
  switch (type) {
    case 'supplier':
      return 'Supplier'
    case 'customer':
      return 'Customer'
    case 'both':
      return 'Both'
    default:
      return type
  }
}

function getPersonTypeSeverity(
  type: PersonType,
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
  switch (type) {
    case 'supplier':
      return 'success'
    case 'customer':
      return 'info'
    case 'both':
      return 'warning'
    default:
      return 'secondary'
  }
}

function formatStatus(status: PersonStatus): string {
  switch (status) {
    case 'lead':
      return 'Lead'
    case 'supplier':
      return 'Supplier'
    case 'customer':
      return 'Customer'
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
    default:
      return status
  }
}

function getStatusSeverity(
  status: PersonStatus,
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
  switch (status) {
    case 'lead':
      return 'warning'
    case 'supplier':
      return 'success'
    case 'customer':
      return 'info'
    case 'active':
      return 'success'
    case 'inactive':
      return 'secondary'
    default:
      return 'secondary'
  }
}
</script>

<style scoped>
.person-card {
  transition: all 0.2s ease-in-out;
}

.person-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.person-avatar {
  flex-shrink: 0;
}

.person-info {
  flex: 1;
  min-width: 0;
}
</style>
